// /2026-start funnel — design-polish bundle A/B test.
//
// Bundles the reachable presentational changes from PR #118 ("Polish /2026
// funnel design + conversion details") into a single treatment arm, scoped to
// the /2026-start acquisition funnel only. We test the bundle as one unit to
// answer "does this polish move conversion on 2026-start?"; it intentionally
// can't attribute which individual tweak won (a follow-up isolates the winner).
//
// Treatment changes (control = current /2026-start behavior):
//   - s0_hero CTA "Continue" → "Get my plan"            (screens-phase1.jsx)
//   - s8 calc-loader "28,431" thousands separator        (screens-phase2.jsx)
//   - s47 email gate: Enter-to-submit, email keyboard
//     hints (enterKeyHint/autoCapitalize/autoCorrect),
//     lime focus ring                                    (screens-phase7.jsx)
//   - PaywallV2 personalized plan recap on s49           (screens-phase8-v2.jsx)
//   - PrimaryButton touch press-feedback                 (shared.jsx)
//
// NOT included: the PR's s15c habits-subtitle copy — s15c is not in the
// 2026-start screen order, so it is unreachable on this funnel.
//
// Hypothesis:
//   The bundle reduces friction (email keyboard + Enter submit, tactile
//   buttons) and restores quiz→paywall personalization continuity (CTA names
//   the payoff, paywall recaps the user's own answers), lifting Welcome →
//   Checkout Completed on the 2026-start paywall-acquisition surface.
//
// Primary metric (analyze in Amplitude):
//   Funnel: `Web Funnel - Welcome Viewed` → `Web Funnel - Checkout Completed`
//   grouped by `event_properties.experiments.start_polish_v1`
//
// Secondary metrics worth watching:
//   - Welcome Viewed → Email Submitted     (s47 lead-capture lift)
//   - Paywall Reached → Plan Selected       (recap engagement)
//   - Paywall Reached → Checkout Completed   (close rate)
//
// Targeting:
//   /2026-start only, enforced at RENDER TIME (local-eval rejects user-property
//   segment targeting — see welcome_2026_headline_v1 for the same constraint).
//   Exposure fires from a SINGLE <Experiment> wrap on the s0_hero CTA — the
//   first screen 100% of 2026-start users see — so every downstream event is
//   tagged with this flag. All other surfaces (loader, email gate, paywall
//   recap, button feedback) read the assignment via peekVariant() and never
//   re-fire exposure. Direct visits to other funnels never mount the s0_hero
//   wrap for this flag, so they stay out of the experiment denominator.
//
// QA overrides:
//   ?exp_override=start_polish_v1:treatment  → force the polish bundle
//   ?exp_override=start_polish_v1:control     → force current behavior
//   Overrides skip exposure firing, so QA traffic doesn't pollute analysis.
//
// Rollout plan:
//   Stage 1: Flip Running at 50/50, 5% rollout in Amplitude UI — smoke-test
//            exposure + variant tags on a handful of users.
//   Stage 2: Bump to 100% rollout — primary read. Aim for ~2000 Welcome
//            Viewed per variant before reading the conversion delta.
//   Stage 3: Roll out the winner to 100% in the UI; retire the conditional
//            renders (or promote them to 100%) in a follow-up cleanup PR.

import { defineExperiment } from '../src/lib/experiments/define';

export default defineExperiment({
  key: 'start_polish_v1',
  name: '/2026-start — design-polish bundle',
  description:
    'Tests the reachable PR #118 design-polish bundle (hero CTA copy, s8 loader number format, s47 email-gate UX, PaywallV2 plan recap, button touch feedback) against current /2026-start behavior. Scoped to /2026-start at render time; exposure fires once at the s0_hero CTA. Primary metric is Welcome Viewed → Checkout Completed.',
  evaluationMode: 'local',
  bucketingKey: 'amplitude_id',
  bucketingUnit: 'User',
  variants: [
    { key: 'control', description: 'Current /2026-start funnel (no polish bundle).' },
    { key: 'treatment', description: 'PR #118 polish bundle: hero CTA "Get my plan", loader thousands separator, email-gate UX, paywall plan recap, button touch feedback.' },
  ],
  // Rolled out to control (100/0) in the Amplitude UI. Weights mirror that
  // post-rollout state so the sync doesn't flag unpatchable drift. The
  // treatment renders stay in code until a follow-up cleanup retires them.
  rolloutWeights: { control: 100, treatment: 0 },
  mutualExclusionGroup: 'global',
  // targetSegments intentionally omitted — local-eval rejects user-property
  // segment shapes. Scoping is enforced at render time via the 2026-start slug
  // check (see startPolishTreatment() in shared.jsx + the s0_hero wrap).
  experimentType: 'a-b-test',
  tags: ['2026-start', 'polish', 'design', 'pr-118'],
  primaryMetric: 'welcome_viewed_to_checkout_completed',
});
